import { useSearchParams } from 'react-router-dom'

/**
 * Which automated record a Track Record view shows: the signal engine's
 * closed signals or the live Paper breakout forward test. The two are never
 * mixed: a view shows one or the other, and an empty Paper record stays empty.
 */
export type RecordSource = 'engine' | 'paper'

export const RECORD_SOURCES: Record<RecordSource, { name: string; caption?: string }> = {
  engine: { name: 'Signal engine' },
  paper: { name: 'Paper', caption: 'live forward test (in progress)' },
}

/*
 * TODO(before public / subscriber launch): flip the default back to 'paper'
 * and put Paper first in RECORD_SOURCE_ORDER.
 *
 * The signal engine's record is a losing one (about -146% cumulative when this
 * default was set on 2026-09-17, and backtesting found no edge in it). It
 * opens first only while the product is in internal review. A
 * subscriber's first look at the track record should be the live Paper
 * forward test, with the signal engine one click away, still shown in full.
 */
export const DEFAULT_RECORD_SOURCE: RecordSource = 'engine'
export const RECORD_SOURCE_ORDER: readonly RecordSource[] = ['engine', 'paper']

const PARAM = 'record'

/** The Track Record page opened on `source`; the default needs no query string. */
export const trackRecordHref = (source: RecordSource) =>
  source === DEFAULT_RECORD_SOURCE ? '/track-record' : `/track-record?${PARAM}=${source}`

/** The selected record, kept in the URL (?record=paper) so a shared link opens the same one. */
export const useRecordSource = (): [RecordSource, (next: RecordSource) => void] => {
  const [params, setParams] = useSearchParams()
  const requested = params.get(PARAM)
  const source: RecordSource = requested === 'engine' || requested === 'paper' ? requested : DEFAULT_RECORD_SOURCE
  const select = (next: RecordSource) =>
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev)
        if (next === DEFAULT_RECORD_SOURCE) updated.delete(PARAM)
        else updated.set(PARAM, next)
        return updated
      },
      { replace: true }
    )
  return [source, select]
}

/** "Signal engine" or "Paper · live forward test (in progress)". */
export const recordLabel = (source: RecordSource) => {
  const { name, caption } = RECORD_SOURCES[source]
  return caption ? `${name} · ${caption}` : name
}

/** Signal engine / Paper switch, in the Dashboard's Signal engine / Demo / Paper toggle style. */
export const RecordToggle = ({
  source,
  onChange,
}: {
  source: RecordSource
  onChange: (next: RecordSource) => void
}) => (
  <div className="flex flex-col items-start gap-1">
    <div
      role="group"
      aria-label="Record shown"
      className="inline-flex rounded-md border border-terminal-border bg-terminal-surface p-0.5"
    >
      {RECORD_SOURCE_ORDER.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={source === option}
          onClick={() => onChange(option)}
          className={`rounded px-3 py-1 text-sm font-medium ${
            source === option
              ? option === 'paper'
                ? 'bg-violet-500 text-gray-950'
                : 'bg-primary-600 text-white'
              : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
          }`}
        >
          {RECORD_SOURCES[option].name}
          {RECORD_SOURCES[option].caption && (
            <span className="hidden font-normal opacity-80 sm:inline"> · {RECORD_SOURCES[option].caption}</span>
          )}
        </button>
      ))}
    </div>
    {/* Phones drop the captions from the buttons; the selected record's full label reads out here. */}
    {RECORD_SOURCES[source].caption && (
      <p className="text-[11px] text-gray-400 sm:hidden" data-testid="record-caption">
        {recordLabel(source)}
      </p>
    )}
  </div>
)
